"use client";

import { useState, useEffect, useCallback, useRef } from "react";
import { useSession } from "next-auth/react";
import { useAuthenticatedFetch } from "@/hooks/useAuthenticatedFetch";
import { Cpu, X, ExternalLink, AlertCircle } from "lucide-react";
import { cn } from "@/lib/utils";

const API_BASE = process.env.NEXT_PUBLIC_AWS_API_URL || "";

interface GpuStatus {
  gpu: {
    running: boolean;
    desired: number;
    actual: number;
  };
  estimatedCost: {
    hourly: number;
    daily: number;
    monthly: number;
  };
}

interface GpuStatusIndicatorProps {
  className?: string;
}

export function GpuStatusIndicator({ className }: GpuStatusIndicatorProps) {
  const { data: session } = useSession();
  const { fetchWithAuth, isAuthenticated } = useAuthenticatedFetch();
  const [status, setStatus] = useState<GpuStatus | null>(null);
  const [expanded, setExpanded] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [startTime, setStartTime] = useState<Date | null>(null);

  // Use ref to track startTime without causing useCallback to re-create
  // This prevents infinite loop: fetchStatus -> setStartTime -> fetchStatus recreated -> useEffect re-runs
  const startTimeRef = useRef<Date | null>(null);

  const fetchStatus = useCallback(async () => {
    if (!isAuthenticated) return;

    try {
      const data = await fetchWithAuth<GpuStatus>(`${API_BASE}/gpu-control`);
      setStatus(data);
      setError(null);

      // Track when GPU started running (for runtime calculation)
      // Use ref to check current value without depending on it
      if (data.gpu.running && !startTimeRef.current) {
        const now = new Date();
        startTimeRef.current = now;
        setStartTime(now);
      } else if (!data.gpu.running) {
        startTimeRef.current = null;
        setStartTime(null);
      }
    } catch (e) {
      // Silently fail for non-admin users (403) or auth issues (401) - don't show indicator
      if (e instanceof Error && (e.message.includes("403") || e.message.includes("401"))) {
        setStatus(null);
        return;
      }
      setError(e instanceof Error ? e.message : "Failed to fetch GPU status");
    }
  }, [isAuthenticated, fetchWithAuth]); // Removed startTime - use ref instead

  // Fetch status on mount and every 60 seconds
  // FIX: Properly cleanup interval on unmount to prevent memory leak
  useEffect(() => {
    if (!session?.user?.email) return;

    fetchStatus();
    const interval = setInterval(fetchStatus, 60000);

    // Cleanup function to clear interval when component unmounts or dependencies change
    return () => {
      clearInterval(interval);
    };
  }, [session?.user?.email, fetchStatus]);

  // Calculate running time
  const getRunningTime = () => {
    if (!startTime) return null;
    const now = new Date();
    const diffMs = now.getTime() - startTime.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const hours = Math.floor(diffMins / 60);
    const mins = diffMins % 60;
    return hours > 0 ? `${hours}h ${mins}m` : `${mins}m`;
  };

  // Don't render if not authenticated, no status, or GPU not running
  if (!session?.user?.email || !status || !status.gpu.running) {
    return null;
  }

  const runningTime = getRunningTime();

  return (
    <div
      className={cn(
        "fixed bottom-4 right-4 z-50 transition-all duration-300",
        className
      )}
    >
      {expanded ? (
        // Expanded card view
        <div className="bg-gray-900 border border-red-500/50 rounded-lg shadow-xl p-4 min-w-[280px] animate-in slide-in-from-bottom-2">
          <div className="flex justify-between items-start mb-3">
            <div className="flex items-center gap-2">
              <div className="relative">
                <Cpu className="w-5 h-5 text-red-500" />
                <span className="absolute -top-1 -right-1 w-2.5 h-2.5 bg-red-500 rounded-full animate-pulse" />
              </div>
              <span className="text-white font-medium">GPU Running</span>
            </div>
            <button
              onClick={() => setExpanded(false)}
              className="text-gray-400 hover:text-white transition-colors"
              aria-label="Close GPU status"
            >
              <X className="w-4 h-4" />
            </button>
          </div>

          <div className="space-y-2 text-sm">
            {/* Cost information */}
            <div className="flex justify-between items-center py-1.5 border-b border-gray-700">
              <span className="text-gray-400">Hourly Cost</span>
              <span className="text-yellow-400 font-medium">
                ${status.estimatedCost.hourly.toFixed(2)}/hr
              </span>
            </div>

            <div className="flex justify-between items-center py-1.5 border-b border-gray-700">
              <span className="text-gray-400">Daily Cost (24h)</span>
              <span className="text-yellow-400">
                ${status.estimatedCost.daily.toFixed(2)}
              </span>
            </div>

            {runningTime && (
              <div className="flex justify-between items-center py-1.5 border-b border-gray-700">
                <span className="text-gray-400">Running Time</span>
                <span className="text-white">{runningTime}</span>
              </div>
            )}

            <div className="flex justify-between items-center py-1.5">
              <span className="text-gray-400">Instances</span>
              <span className="text-white">
                {status.gpu.actual} / {status.gpu.desired}
              </span>
            </div>
          </div>

          {/* Warning message */}
          <div className="mt-3 p-2 bg-red-500/10 border border-red-500/30 rounded text-xs text-red-300 flex gap-2">
            <AlertCircle className="w-4 h-4 shrink-0 mt-0.5" />
            <span>
              GPU is running and incurring charges. Stop it when not in use.
            </span>
          </div>

          {/* Link to admin control */}
          <a
            href="/admin/admin-control"
            className="mt-3 flex items-center justify-center gap-2 w-full py-2 bg-red-600 hover:bg-red-700 text-white text-sm rounded transition-colors"
          >
            <span>Manage GPU</span>
            <ExternalLink className="w-3.5 h-3.5" />
          </a>
        </div>
      ) : (
        // Collapsed pill view
        <button
          onClick={() => setExpanded(true)}
          className="flex items-center gap-2 bg-red-600 hover:bg-red-700 text-white px-4 py-2.5 rounded-full shadow-lg transition-all hover:scale-105 animate-in slide-in-from-bottom-2"
          aria-label="GPU is running - click for details"
        >
          <div className="relative">
            <Cpu className="w-4 h-4" />
            <span className="absolute -top-0.5 -right-0.5 w-2 h-2 bg-white rounded-full animate-pulse" />
          </div>
          <span className="font-medium">GPU Running</span>
          <span className="text-red-200 text-sm">
            ${status.estimatedCost.hourly.toFixed(2)}/hr
          </span>
        </button>
      )}
    </div>
  );
}
